import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Button, Row, Col, Typography, Spin, Carousel } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  storefrontApi,
  type HomeData,
  type CategoryItem,
  type BannerItem,
} from '@/apis/storefrontApi';
import { getStorefrontIcon } from '@/utils/storefrontIcons';
import styles from './index.module.less';

const { Title, Paragraph } = Typography;

function isExternal(path?: string): boolean {
  return !!path && /^(https?:)?\/\//.test(path);
}

function ActionLink({
  to,
  className,
  children,
}: {
  to?: string;
  className?: string;
  children: React.ReactNode;
}) {
  if (!to) return <span className={className}>{children}</span>;
  if (isExternal(to)) {
    return (
      <a href={to} className={className} target='_blank' rel='noreferrer'>
        {children}
      </a>
    );
  }
  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
}

function toHighlights(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(
      (item): item is string => typeof item === 'string' && item.trim().length > 0
    );
  }
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value);
      return toHighlights(parsed);
    } catch {
      return [];
    }
  }
  return [];
}

function StoryCarousel({
  items,
}: {
  items: { image: string; label: string; link?: string }[];
}) {
  const [page, setPage] = useState(0);
  const [visibleCount, setVisibleCount] = useState(4);

  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      if (w < 640) setVisibleCount(1);
      else if (w < 1024) setVisibleCount(2);
      else setVisibleCount(4);
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);

  const pageCount = Math.max(1, Math.ceil(items.length / visibleCount));
  const safePage = Math.min(page, pageCount - 1);

  useEffect(() => {
    setPage(p => Math.min(p, Math.max(0, pageCount - 1)));
  }, [pageCount]);

  return (
    <div className={styles.storyCarousel}>
      <div className={styles.storyViewport}>
        <div
          className={styles.storyTrack}
          style={{ transform: `translateX(-${safePage * 100}%)` }}
        >
          {Array.from({ length: pageCount }).map((_, p) => (
            <div className={styles.storyPage} key={p}>
              {items.slice(p * visibleCount, p * visibleCount + visibleCount).map((item, i) => {
                const card = (
                  <div className={styles.storyCard}>
                    {item.image ? (
                      <img src={item.image} alt={item.label} className={styles.storyImg} />
                    ) : (
                      <div className={styles.storyPlaceholder} />
                    )}
                    {item.label && (
                      <div className={styles.storyLabel}>{item.label}</div>
                    )}
                  </div>
                );
                return (
                  <div className={styles.storySlide} key={`${p}-${i}`}>
                    {item.link ? (
                      <ActionLink to={item.link} className={styles.storyLink}>
                        {card}
                      </ActionLink>
                    ) : (
                      card
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      {pageCount > 1 && (
        <div className={styles.storyDots}>
          {Array.from({ length: pageCount }).map((_, i) => (
            <button
              key={i}
              type='button'
              className={i === safePage ? styles.storyDotActive : styles.storyDot}
              onClick={() => setPage(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const DEFAULT_ABOUT = {
  mediaType: 'image' as const,
  mediaUrl: '',
  title: 'About Sinolinkway',
  paragraph1:
    'Sinolinkway supplies industrial and home solutions covering small appliances, electrical equipment, hardware and solar products for professionals and households.',
  paragraph2:
    'From sourcing to after-sales support, we help partners get reliable products, competitive lead times and consistent quality across every order.',
  buttonText: 'Explore More',
  buttonPath: '/about',
};

const DEFAULT_SERVICES = [
  {
    icon: 'TrophyOutlined',
    title: 'Superior Quality',
    description:
      'Certified materials and finished goods, inspected to keep every shipment consistent.',
  },
  {
    icon: 'CustomerServiceOutlined',
    title: 'Fast Response',
    description:
      'Our professional service team responds quickly to quotes, samples and technical questions.',
  },
  {
    icon: 'StarOutlined',
    title: 'Strict Inspection',
    description:
      'Incoming and outgoing checks on key specs so products arrive ready for your line.',
  },
  {
    icon: 'CarOutlined',
    title: 'Fast Delivery',
    description:
      'Coordinated packing and logistics to shorten lead time from factory to your warehouse.',
  },
];

const DEFAULT_CATEGORIES: CategoryItem[] = [
  {
    key: 'appliances',
    title: 'Small Appliances',
    subtitle: 'Household & kitchen appliances',
    description: 'Household & kitchen appliances',
    icon: 'AppstoreOutlined',
    path: '/products?category=appliances',
    highlights: [],
  },
  {
    key: 'electrical',
    title: 'Electrical',
    subtitle: 'Power & wiring solutions',
    description: 'Power & wiring solutions',
    icon: 'ThunderboltOutlined',
    path: '/products?category=electrical',
    highlights: [],
  },
  {
    key: 'hardware',
    title: 'Hardware',
    subtitle: 'Tools & fittings',
    description: 'Tools & fittings',
    icon: 'ToolOutlined',
    path: '/products?category=hardware',
    highlights: [],
  },
  {
    key: 'solar',
    title: 'Solar & PV',
    subtitle: 'Photovoltaic products',
    description: 'Photovoltaic products',
    icon: 'SunOutlined',
    path: '/products?category=solar',
    highlights: [],
  },
];

const Home: React.FC = () => {
  const { i18n } = useTranslation();
  const [homeData, setHomeData] = useState<HomeData | null>(null);
  const [categories, setCategories] = useState<CategoryItem[]>([]);
  const [banners, setBanners] = useState<BannerItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      storefrontApi.getHome(),
      storefrontApi.getCategories(),
      storefrontApi.getBanners('home').catch(() => []),
    ])
      .then(([home, cats, bannerList]) => {
        setHomeData(home ?? null);
        setCategories(Array.isArray(cats) ? cats : []);
        setBanners(Array.isArray(bannerList) ? bannerList : []);
      })
      .catch(err => {
        if (import.meta.env.DEV) {
          console.warn('[Home] 接口请求失败，使用默认文案', err);
        }
      })
      .finally(() => setLoading(false));
  }, [i18n.language]);

  const categoryList = useMemo(
    () => (categories.length ? categories : DEFAULT_CATEGORIES),
    [categories]
  );

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingWrap}>
          <Spin size='large' />
        </div>
      </div>
    );
  }

  const hero = homeData?.hero ?? {
    title: 'Industrial & Home Solutions',
    description:
      'Quality small appliances, electrical, hardware and solar products for professionals and households.',
    primaryButton: { text: 'View Products', path: '/products' },
    secondaryButton: { text: 'Contact Us', path: '/contact' },
  };
  const categoriesSection = homeData?.categoriesSection ?? {
    title: 'Product Categories',
    description: 'Browse by category. Choose the right products for your needs.',
    moreText: 'Explore More',
  };
  const moreText = categoriesSection.moreText || 'Explore More';
  const aboutIntro = {
    ...DEFAULT_ABOUT,
    ...(homeData?.aboutIntro ?? {}),
  };
  const serviceItems = (
    homeData?.services?.items?.length ? homeData.services.items : DEFAULT_SERVICES
  ).filter(item => item?.title || item?.description);
  const featured = homeData?.featuredProducts;
  const featuredItems = (featured?.items ?? []).filter(item => item?.title || item?.image);
  const stories = homeData?.projectStories;
  const storyItems = (stories?.items ?? []).filter(item => item?.image || item?.label);
  const cta = homeData?.cta ?? {
    title: 'Need a quote or support?',
    description:
      'Get in touch for bulk orders, technical support or partnership inquiries.',
    buttonText: 'Contact Us',
    buttonPath: '/contact',
  };

  const bannerList = banners.filter(b => b.image);
  const hasBanner = bannerList.length > 0;
  const showAbout = !!(
    aboutIntro.title ||
    aboutIntro.paragraph1 ||
    aboutIntro.mediaUrl
  );

  return (
    <div className={styles.page}>
      <section className={hasBanner ? styles.heroMerged : styles.hero}>
        {hasBanner ? (
          <>
            <div className={styles.heroBannerBox}>
              <div className={styles.bannerWrap}>
                <Carousel
                  className={styles.bannerCarousel}
                  autoplay
                  effect='fade'
                  dots={{ className: styles.bannerDots }}
                >
                  {bannerList.map(b => {
                    const desktopSrc = b.imageDesktop || b.image;
                    const mobileSrc = b.imageMobile || b.image;
                    const imgEl = (
                      <picture>
                        <source
                          media='(min-width: 768px)'
                          srcSet={desktopSrc}
                        />
                        <img
                          src={mobileSrc}
                          alt={b.title ?? ''}
                          className={styles.bannerImg}
                        />
                      </picture>
                    );
                    return (
                      <div key={b.id} className={styles.bannerSlide}>
                        {b.link ? (
                          <Link to={b.link} className={styles.bannerLink}>
                            {imgEl}
                          </Link>
                        ) : (
                          imgEl
                        )}
                      </div>
                    );
                  })}
                </Carousel>
              </div>
              <div className={styles.heroOverlay}>
                <div className={styles.heroInner}>
                  <Title level={1} className={styles.heroTitle}>
                    {hero.title}
                  </Title>
                  <Paragraph className={styles.heroDesc}>
                    {hero.description}
                  </Paragraph>
                  <div className={styles.heroActions}>
                    <Link to={hero.primaryButton.path}>
                      <Button
                        type='primary'
                        size='large'
                        icon={<ArrowRightOutlined />}
                      >
                        {hero.primaryButton.text}
                      </Button>
                    </Link>
                    <Link to={hero.secondaryButton.path}>
                      <Button size='large' className={styles.heroBtnSecondary}>
                        {hero.secondaryButton.text}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className={styles.heroInner}>
            <Title level={1} className={styles.heroTitle}>
              {hero.title}
            </Title>
            <Paragraph className={styles.heroDesc}>
              {hero.description}
            </Paragraph>
            <div className={styles.heroActions}>
              <Link to={hero.primaryButton.path}>
                <Button
                  type='primary'
                  size='large'
                  icon={<ArrowRightOutlined />}
                >
                  {hero.primaryButton.text}
                </Button>
              </Link>
              <Link to={hero.secondaryButton.path}>
                <Button size='large'>{hero.secondaryButton.text}</Button>
              </Link>
            </div>
          </div>
        )}
      </section>

      <section className={styles.section}>
        <div className={styles.sectionInner}>
          <Title level={2} className={styles.sectionTitle}>
            {categoriesSection.title}
          </Title>
          <Paragraph className={styles.sectionDesc}>
            {categoriesSection.description}
          </Paragraph>
          <Row gutter={[24, 24]}>
            {categoryList.map(cat => {
              const highlights = toHighlights(cat.highlights);
              const subtitle = cat.subtitle || cat.description;
              return (
                <Col xs={24} sm={12} md={6} key={cat.key}>
                  <ActionLink
                    to={
                      cat.key
                        ? `/products?category=${encodeURIComponent(cat.key)}`
                        : cat.path || '/products'
                    }
                    className={styles.categoryLink}
                  >
                    <div className={styles.categoryCard}>
                      <div className={styles.categoryMedia}>
                        {cat.image ? (
                          <img
                            src={cat.image}
                            alt={cat.title}
                            className={styles.categoryImg}
                          />
                        ) : (
                          <div className={styles.categoryIconFallback}>
                            {getStorefrontIcon(cat.icon)}
                          </div>
                        )}
                      </div>
                      <div className={styles.categoryBody}>
                        <h3 className={styles.categoryTitle}>{cat.title}</h3>
                        {subtitle && (
                          <p className={styles.categorySubtitle}>{subtitle}</p>
                        )}
                        {highlights.length > 0 && (
                          <ul className={styles.categoryList}>
                            {highlights.map(item => (
                              <li key={item}>{item}</li>
                            ))}
                          </ul>
                        )}
                        <span className={styles.categoryMore}>{moreText}</span>
                      </div>
                    </div>
                  </ActionLink>
                </Col>
              );
            })}
          </Row>
        </div>
      </section>

      {showAbout && (
        <section className={styles.aboutSection}>
          <div className={styles.sectionInner}>
            <Row gutter={[40, 32]} align='middle'>
              <Col xs={24} md={12}>
                <div className={styles.aboutMedia}>
                  {aboutIntro.mediaUrl && aboutIntro.mediaType === 'video' ? (
                    <video
                      className={styles.aboutVideo}
                      src={aboutIntro.mediaUrl}
                      controls
                      playsInline
                    />
                  ) : aboutIntro.mediaUrl ? (
                    <img
                      src={aboutIntro.mediaUrl}
                      alt={aboutIntro.title || ''}
                      className={styles.aboutImg}
                    />
                  ) : (
                    <div className={styles.aboutPlaceholder} />
                  )}
                </div>
              </Col>
              <Col xs={24} md={12}>
                <div className={styles.aboutCopy}>
                  {aboutIntro.title && (
                    <Title level={2} className={styles.aboutTitle}>
                      {aboutIntro.title}
                    </Title>
                  )}
                  {aboutIntro.paragraph1 && (
                    <p className={styles.aboutText}>{aboutIntro.paragraph1}</p>
                  )}
                  {aboutIntro.paragraph2 && (
                    <p className={styles.aboutText}>{aboutIntro.paragraph2}</p>
                  )}
                  {aboutIntro.buttonText && aboutIntro.buttonPath && (
                    <ActionLink to={aboutIntro.buttonPath}>
                      <Button type='primary' size='large' className={styles.aboutBtn}>
                        {aboutIntro.buttonText}
                      </Button>
                    </ActionLink>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        </section>
      )}

      {serviceItems.length > 0 && (
        <section className={styles.servicesBar}>
          <div className={styles.sectionInner}>
            <Row gutter={[24, 24]}>
              {serviceItems.map((item, i) => (
                <Col xs={24} sm={12} md={6} key={`${item.title}-${i}`}>
                  <div className={styles.serviceItem}>
                    <div className={styles.serviceIcon}>
                      {getStorefrontIcon(item.icon)}
                    </div>
                    <h3 className={styles.serviceTitle}>{item.title}</h3>
                    <p className={styles.serviceDesc}>{item.description}</p>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </section>
      )}

      {featuredItems.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionInner}>
            <Title level={2} className={styles.sectionTitle}>
              {featured?.title || 'FEATURED PRODUCTS'}
            </Title>
            {featured?.eyebrow && (
              <Paragraph className={styles.sectionDesc}>
                {featured.eyebrow}
              </Paragraph>
            )}
            <Row gutter={[24, 32]}>
              {featuredItems.map((item, i) => (
                <Col xs={24} sm={12} md={6} key={item.link || `${item.title}-${i}`}>
                  <div className={styles.featuredCard}>
                    <ActionLink to={item.link || '/contact'} className={styles.featuredMediaLink}>
                      <div className={styles.featuredMedia}>
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.title}
                            className={styles.featuredImg}
                          />
                        ) : (
                          <div className={styles.featuredPlaceholder} />
                        )}
                      </div>
                      <h3 className={styles.featuredName}>{item.title}</h3>
                    </ActionLink>
                    <ActionLink to={item.link || '/contact'} className={styles.featuredBtnWrap}>
                      <Button type='primary' className={styles.featuredBtn} block>
                        {featured?.buttonText || 'Inquire Now'}
                      </Button>
                    </ActionLink>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </section>
      )}

      {storyItems.length > 0 && (
        <section className={styles.section}>
          <div className={styles.sectionInner}>
            <Title level={2} className={styles.sectionTitle}>
              {stories?.title || 'Project Success Stories'}
            </Title>
            {stories?.description && (
              <Paragraph className={styles.sectionDesc}>
                {stories.description}
              </Paragraph>
            )}
            <StoryCarousel items={storyItems} />
          </div>
        </section>
      )}

      <section className={styles.cta}>
        <div className={styles.ctaInner}>
          <Title level={2} className={styles.sectionTitle}>
            {cta.title}
          </Title>
          <Paragraph className={styles.sectionDesc}>{cta.description}</Paragraph>
          <Link to={cta.buttonPath}>
            <Button type='primary' size='large'>
              {cta.buttonText}
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
